import { useRef, useEffect, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Button,
    Alert
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';

const ServiceFileUpload = ({ files, setFiles, maxFiles = 4, maxTotalSizeMB = 1 }) => {
    const fileInputRef = useRef(null);
    const [previewUrls, setPreviewUrls] = useState([]);
    const [error, setError] = useState('');

    const maxTotalSizeBytes = maxTotalSizeMB * 1024 * 1024;

    // Calculate current total size
    const currentTotalSize = useMemo(() => {
        return files.reduce((total, file) => total + file.size, 0);
    }, [files]);

    // Create and cleanup blob URLs for previews
    useEffect(() => {
        const urls = files.map(file => URL.createObjectURL(file));
        setPreviewUrls(urls);

        // Cleanup: revoke URLs when component unmounts or files change
        return () => {
            urls.forEach(url => URL.revokeObjectURL(url));
        };
    }, [files]);

    const handleFileSelect = (event) => {
        setError('');
        const selectedFiles = Array.from(event.target.files);

        // Filter for valid image files
        const imageFiles = selectedFiles.filter(file => file.type.startsWith('image/'));

        if (imageFiles.length === 0) {
            event.target.value = '';
            return;
        }

        // Check how many more files we can add
        const remainingSlots = maxFiles - files.length;
        if (remainingSlots <= 0) {
            setError(`Maximum ${maxFiles} images allowed.`);
            event.target.value = '';
            return;
        }

        // Limit to remaining slots
        const filesToAdd = imageFiles.slice(0, remainingSlots);

        // Calculate new total size
        const newFilesSize = filesToAdd.reduce((total, file) => total + file.size, 0);
        const projectedTotalSize = currentTotalSize + newFilesSize;

        if (projectedTotalSize > maxTotalSizeBytes) {
            setError(`Image Size Is Too Large.`);
            event.target.value = '';
            return;
        }

        setFiles([...files, ...filesToAdd]);

        // Reset input to allow selecting same file again
        event.target.value = '';
    };

    const handleRemoveFile = (index) => {
        setFiles(files.filter((_, i) => i !== index));
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Box
                sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 2,
                }}
            >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Please upload any relevant photos to help us understand the service need.
                </Typography>
                <Typography variant="caption" sx={{ color: 'grey.600', display: 'block', mb: 1 }}>
                    Upload up to {maxFiles} images. Total size limit: Less Than {maxTotalSizeMB} MB
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2, py: 0.5 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {/* Image Preview Grid */}
                {files.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            mb: 2
                        }}
                    >
                        {files.map((file, index) => (
                            <Box
                                key={index}
                                sx={{
                                    position: 'relative',
                                    width: 80,
                                    height: 80,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    border: '1px solid #ddd',
                                }}
                            >
                                {previewUrls[index] && (
                                    <img
                                        src={previewUrls[index]}
                                        alt={`Upload ${index + 1}`}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                )}
                                <IconButton
                                    size="small"
                                    onClick={() => handleRemoveFile(index)}
                                    sx={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        bgcolor: 'rgba(0,0,0,0.6)',
                                        color: 'white',
                                        padding: '2px',
                                        '&:hover': {
                                            bgcolor: 'rgba(0,0,0,0.8)',
                                        },
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Add File Button */}
                <Button
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    onClick={handleClick}
                    disabled={files.length >= maxFiles}
                    sx={{
                        textTransform: 'none',
                        borderColor: '#ddd',
                        color: 'text.secondary',
                        '&:hover': {
                            borderColor: '#bbb',
                            bgcolor: 'grey.50',
                        },
                    }}
                >
                    Add file
                </Button>

                {/* Hidden File Input */}
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                />
            </Box>
        </Box>
    );
};

export default ServiceFileUpload;
